import React, { useState, useEffect } from 'react';

const API = '';
const BASE_FOLDERS = ['长期日常', '事件快照', 'RP剧本', '人物档案'];

// 空白草稿
function blankDraft(folder) {
  return { folder: folder && folder !== '全部' ? folder : '长期日常', customFolder: '', title: '', keywords: '', secondaryKeys: '', secondaryLogic: 'AND_ANY', sticky: 0, realm: 'all', content: '', priority: 5 };
}

function Worldbook() {
  const [items, setItems] = useState([]);
  const [folders, setFolders] = useState(BASE_FOLDERS);
  const [activeFolder, setActiveFolder] = useState('全部');
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);   // 展开编辑中的条目id，'new'=新建草稿
  const [draft, setDraft] = useState(null);              // 当前展开条目的编辑副本
  const [msg, setMsg] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [search, setSearch] = useState('');
  const [showTest, setShowTest] = useState(false);
  const [testText, setTestText] = useState('');
  const [testResults, setTestResults] = useState(null);
  const [testing, setTesting] = useState(false);

  useEffect(function() { fetchAll(); }, []);

  var fetchAll = async function() {
    try {
      var [itemsResp, foldersResp] = await Promise.all([
        fetch(API + '/api/worldbook'),
        fetch(API + '/api/worldbook/folders')
      ]);
      var itemsData = await itemsResp.json();
      var foldersData = await foldersResp.json();
      setItems(Array.isArray(itemsData) ? itemsData : []);
      if (Array.isArray(foldersData) && foldersData.length > 0) setFolders(foldersData);
    } catch (e) { console.error('Failed to load worldbook:', e); }
    setLoading(false);
  };

  var flash = function(text) {
    setMsg(text);
    setTimeout(function() { setMsg(''); }, 4000);
  };

  var visibleItems = activeFolder === '全部' ? items : items.filter(function(it) { return it.folder === activeFolder; });
  if (search.trim()) {
    var q = search.trim().toLowerCase();
    visibleItems = visibleItems.filter(function(it) {
      var kwStr = Array.isArray(it.keywords) ? it.keywords.join(' ') : '';
      return (it.title || '').toLowerCase().indexOf(q) !== -1
        || kwStr.toLowerCase().indexOf(q) !== -1
        || (it.content || '').toLowerCase().indexOf(q) !== -1;
    });
  }
  var activeCount = items.filter(function(it) { return it.is_active; }).length;
  var totalHits = items.reduce(function(s, it) { return s + (it.hit_count || 0); }, 0);

  // ---- 展开/收起 ----
  var openEntry = function(item) {
    if (expandedId === item.id) { setExpandedId(null); setDraft(null); return; }
    var known = folders.indexOf(item.folder) !== -1;
    setExpandedId(item.id);
    setDraft({
      folder: known ? item.folder : '__custom__',
      customFolder: known ? '' : (item.folder || ''),
      title: item.title || '',
      keywords: Array.isArray(item.keywords) ? item.keywords.join(', ') : (item.keywords || ''),
      secondaryKeys: Array.isArray(item.secondary_keys) ? item.secondary_keys.join(', ') : (item.secondary_keys || ''),
      secondaryLogic: item.secondary_logic || 'AND_ANY',
      sticky: item.sticky || 0,
      realm: item.realm || 'all',
      content: item.content || '',
      priority: item.priority || 5
    });
  };

  var openNew = function() {
    if (expandedId === 'new') { setExpandedId(null); setDraft(null); return; }
    setExpandedId('new');
    setDraft(blankDraft(activeFolder));
  };

  var closeEditor = function() { setExpandedId(null); setDraft(null); };

  var resolveFolder = function(d) {
    if (d.folder === '__custom__') return d.customFolder.trim() || '长期日常';
    return d.folder;
  };

  // ---- 保存 ----
  var saveDraft = async function() {
    if (!draft) return;
    if (!draft.title.trim() || !draft.content.trim()) { flash('标题和内容必填'); return; }
    if (!draft.keywords.trim()) { flash('触发词必填——没有触发词的条目永远不会醒来'); return; }
    var body = { folder: resolveFolder(draft), title: draft.title.trim(), content: draft.content, keywords: draft.keywords, secondary_keys: draft.secondaryKeys, secondary_logic: draft.secondaryLogic, sticky: draft.sticky, realm: draft.realm, priority: draft.priority };
    try {
      var url = expandedId === 'new' ? API + '/api/worldbook' : API + '/api/worldbook/' + expandedId;
      var resp = await fetch(url, {
        method: expandedId === 'new' ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      var data = await resp.json();
      if (data.id) {
        closeEditor();
        flash(expandedId === 'new' ? '已归档' : '已更新');
        setLoading(true);
        fetchAll();
      } else {
        flash(data.error || '保存失败');
      }
    } catch (e) { console.error('Failed to save:', e); flash('保存失败'); }
  };

  var confirmDelete = async function() {
    var id = deleteConfirm;
    setDeleteConfirm(null);
    try {
      await fetch(API + '/api/worldbook/' + id, { method: 'DELETE' });
      setItems(function(prev) { return prev.filter(function(k) { return k.id !== id; }); });
      if (expandedId === id) closeEditor();
    } catch (e) { console.error('Failed to delete:', e); }
  };

  var toggleActive = async function(item, e) {
    e.stopPropagation();
    try {
      await fetch(API + '/api/worldbook/' + item.id, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !item.is_active })
      });
      setItems(function(prev) {
        return prev.map(function(k) {
          if (k.id === item.id) return Object.assign({}, k, { is_active: !k.is_active });
          return k;
        });
      });
    } catch (e) { console.error('Failed to toggle:', e); }
  };

  var runTest = async function() {
    if (!testText.trim()) return;
    setTesting(true);
    setTestResults(null);
    try {
      var resp = await fetch(API + '/api/worldbook/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: testText })
      });
      var data = await resp.json();
      setTestResults(Array.isArray(data) ? data : []);
    } catch (e) { setTestResults([]); }
    setTesting(false);
  };

  // ---- 样式 ----
  var S = {
    page: { padding: '0 0 40px', fontFamily: 'var(--font-sans)' },
    header: { padding: '18px 20px 6px' },
    titleRow: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' },
    title: { fontSize: 24, fontWeight: 700, color: 'var(--text)', fontFamily: 'var(--font-serif)' },
    stats: { fontSize: 11, color: 'var(--text3)', marginTop: 4 },
    btnRow: { display: 'flex', gap: 8 },
    hBtn: { padding: '6px 13px', borderRadius: 16, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text2)', fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font-sans)' },
    hBtnAccent: { padding: '6px 13px', borderRadius: 16, border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font-sans)' },
    folderRow: { display: 'flex', gap: 6, padding: '10px 20px 12px', overflowX: 'auto' },
    folderChip: function(on) {
      return {
        flex: 'none', padding: '4px 12px', borderRadius: 14, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap',
        border: '1px solid ' + (on ? 'var(--accent)' : 'var(--border)'),
        background: on ? 'var(--accent2)' : 'transparent',
        color: on ? 'var(--accent)' : 'var(--text3)',
        fontFamily: 'var(--font-sans)', transition: 'all 0.15s'
      };
    },
    book: { margin: '0 14px', background: 'var(--card)', borderRadius: 14, border: '1px solid var(--border)', overflow: 'hidden' },
    row: { padding: '12px 14px', borderBottom: '1px solid var(--border)', cursor: 'pointer' },
    rowTop: { display: 'flex', alignItems: 'center', gap: 10 },
    dot: function(on) {
      return {
        flex: 'none', width: 9, height: 9, borderRadius: '50%',
        background: on ? '#5a9a80' : 'transparent',
        border: on ? 'none' : '1.5px solid var(--text3)',
        cursor: 'pointer'
      };
    },
    rowTitle: function(on) {
      return { flex: 1, fontSize: 14.5, fontWeight: 500, color: on ? 'var(--text)' : 'var(--text3)', fontFamily: 'var(--font-serif)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
    },
    rowMeta: { flex: 'none', fontSize: 10, color: 'var(--text3)' },
    chev: function(open) {
      return { flex: 'none', color: 'var(--text3)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' };
    },
    kwLine: { marginTop: 4, marginLeft: 19, fontSize: 11, color: 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    kwWord: { color: 'var(--accent)' },
    sceneBadge: { display: 'inline-block', marginLeft: 6, padding: '0 6px', borderRadius: 5, fontSize: 10, border: '1px solid var(--accent)', color: 'var(--accent)' },
    editor: { padding: '4px 14px 14px 14px', background: 'var(--bg2)', borderBottom: '1px solid var(--border)' },
    label: { fontSize: 11, color: 'var(--text3)', margin: '10px 0 4px' },
    input: { width: '100%', boxSizing: 'border-box', padding: '9px 12px', borderRadius: 9, border: '1px solid var(--border)', background: 'var(--card)', color: 'var(--text)', fontSize: 13.5, fontFamily: 'var(--font-sans)', outline: 'none' },
    textarea: { width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 9, border: '1px solid var(--border)', background: 'var(--card)', color: 'var(--text)', fontSize: 13.5, lineHeight: 1.7, fontFamily: 'var(--font-sans)', outline: 'none', resize: 'vertical' },
    editorBtns: { display: 'flex', gap: 8, marginTop: 12 },
    saveBtn: { flex: 1, padding: '10px 0', borderRadius: 9, border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 14, cursor: 'pointer', fontFamily: 'var(--font-sans)' },
    delBtn: { flex: 'none', padding: '10px 16px', borderRadius: 9, border: '1px solid var(--border)', background: 'var(--card)', color: '#c0564a', fontSize: 13, cursor: 'pointer', fontFamily: 'var(--font-sans)' },
    empty: { padding: '36px 0', textAlign: 'center', fontSize: 13, color: 'var(--text3)' }
  };

  var renderEditor = function(isNew) {
    if (!draft) return null;
    return (
      <div style={S.editor} onClick={function(e) { e.stopPropagation(); }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ flex: 2 }}>
            <div style={S.label}>文件夹</div>
            <select
              style={S.input}
              value={draft.folder}
              onChange={function(e) { setDraft(Object.assign({}, draft, { folder: e.target.value })); }}
            >
              {folders.map(function(f) { return <option key={f} value={f}>{f}</option>; })}
              <option value="__custom__">自定义...</option>
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <div style={S.label}>优先级</div>
            <input
              style={S.input}
              type="number"
              value={draft.priority}
              onChange={function(e) { setDraft(Object.assign({}, draft, { priority: parseInt(e.target.value) || 0 })); }}
            />
          </div>
        </div>
        {draft.folder === '__custom__' && (
          <div>
            <div style={S.label}>新文件夹名</div>
            <input
              style={S.input}
              value={draft.customFolder}
              onChange={function(e) { setDraft(Object.assign({}, draft, { customFolder: e.target.value })); }}
            />
          </div>
        )}
        <div style={S.label}>标题</div>
        <input
          style={S.input}
          placeholder="如：惠阳站出租车事件"
          value={draft.title}
          onChange={function(e) { setDraft(Object.assign({}, draft, { title: e.target.value })); }}
        />
        <div style={S.label}>触发词（逗号分隔 · 她提到才醒来 · 选她真的会说出口的词）</div>
        <input
          style={S.input}
          placeholder="如：出租车, 惠阳站, 司机"
          value={draft.keywords}
          onChange={function(e) { setDraft(Object.assign({}, draft, { keywords: e.target.value })); }}
        />
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ flex: 1 }}>
            <div style={S.label}>附加条件</div>
            <select
              style={S.input}
              value={draft.secondaryLogic}
              onChange={function(e) { setDraft(Object.assign({}, draft, { secondaryLogic: e.target.value })); }}
            >
              <option value="AND_ANY">还需同时出现任一</option>
              <option value="NOT_ANY">出现任一则不触发</option>
            </select>
          </div>
          <div style={{ flex: 1.4 }}>
            <div style={S.label}>次要词（逗号分隔 · 留空=不启用）</div>
            <input
              style={S.input}
              value={draft.secondaryKeys}
              onChange={function(e) { setDraft(Object.assign({}, draft, { secondaryKeys: e.target.value })); }}
            />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ flex: 1.4 }}>
            <div style={S.label}>房间</div>
            <select
              style={S.input}
              value={draft.realm}
              onChange={function(e) { setDraft(Object.assign({}, draft, { realm: e.target.value })); }}
            >
              <option value="all">两个房间都生效</option>
              <option value="aiden">仅林晏房</option>
              <option value="silas">仅Silas房</option>
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <div style={S.label}>粘性（命中后保持N轮）</div>
            <input
              style={S.input}
              type="number"
              value={draft.sticky}
              onChange={function(e) { setDraft(Object.assign({}, draft, { sticky: parseInt(e.target.value) || 0 })); }}
            />
          </div>
        </div>
        <div style={S.label}>内容（命中时全文注入 · 永不衰减）</div>
        <textarea
          style={S.textarea}
          placeholder="一整件事的来龙去脉 / 一套RP设定 / 一份人物档案"
          value={draft.content}
          onChange={function(e) { setDraft(Object.assign({}, draft, { content: e.target.value })); }}
          rows={9}
        />
        <div style={S.editorBtns}>
          <button style={S.saveBtn} onClick={saveDraft}>{isNew ? '归档' : '更新'}</button>
          {!isNew && (
            <button style={S.delBtn} onClick={function() { setDeleteConfirm(expandedId); }}>删除</button>
          )}
          <button style={Object.assign({}, S.delBtn, { color: 'var(--text3)' })} onClick={closeEditor}>收起</button>
        </div>
      </div>
    );
  };

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div style={S.titleRow}>
          <div style={S.title}>世界书</div>
          <div style={S.btnRow}>
            <button style={S.hBtn} onClick={function() { setShowTest(!showTest); setTestResults(null); }}>
              {showTest ? '收起测试' : '检索测试'}
            </button>
            <button style={S.hBtnAccent} onClick={openNew}>
              {expandedId === 'new' ? '取消' : '+ 新条目'}
            </button>
          </div>
        </div>
        <div style={S.stats}>
          {items.length} 条 · 启用 {activeCount} · 累计命中 {totalHits} 次 &nbsp;｜&nbsp; 触发词命中才注入全文，不命中零成本
        </div>
      </div>

      <div style={S.folderRow}>
        {['全部'].concat(folders).map(function(f) {
          return (
            <div key={f} style={S.folderChip(activeFolder === f)} onClick={function() { setActiveFolder(f); closeEditor(); }}>
              {f}
            </div>
          );
        })}
      </div>

      <div style={{ margin: '0 14px 10px', position: 'relative' }}>
        <svg style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)' }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        <input
          style={Object.assign({}, S.input, { paddingLeft: 34, borderRadius: 12 })}
          placeholder="搜标题、触发词、内容"
          value={search}
          onChange={function(e) { setSearch(e.target.value); }}
        />
        {search && (
          <div
            style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)', fontSize: 14, cursor: 'pointer', padding: '2px 4px' }}
            onClick={function() { setSearch(''); }}
          >×</div>
        )}
      </div>

      {msg && <div style={{ margin: '0 20px 8px', fontSize: 12, color: 'var(--accent)' }}>{msg}</div>}

      {showTest && (
        <div style={{ margin: '0 14px 12px', padding: 14, background: 'var(--card)', borderRadius: 14, border: '1px solid var(--border)' }}>
          <input
            style={S.input}
            placeholder="输一句她平时会说的话，看谁会醒来"
            value={testText}
            onChange={function(e) { setTestText(e.target.value); }}
          />
          <button style={Object.assign({}, S.saveBtn, { marginTop: 8, width: '100%' })} onClick={runTest} disabled={testing}>
            {testing ? '检索中...' : '测试'}
          </button>
          {testResults !== null && (
            <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--text2)', lineHeight: 1.9 }}>
              {testResults.length === 0 ? '没有条目醒来' : testResults.map(function(r) {
                return (
                  <div key={r.id} style={{ borderBottom: '1px dashed var(--border)', padding: '4px 0' }}>
                    <span style={{ color: 'var(--text)' }}>{r.title}</span>
                    <span style={{ fontSize: 10, color: 'var(--text3)', marginLeft: 6 }}>[{r.folder}]</span>
                    <span style={{ color: 'var(--accent)', marginLeft: 8, fontSize: 11.5 }}>命中：{r.matched.join('、')}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div style={S.book}>
        {expandedId === 'new' && renderEditor(true)}
        {loading ? (
          <div style={S.empty}>翻开中...</div>
        ) : visibleItems.length === 0 && expandedId !== 'new' ? (
          <div style={S.empty}>{search.trim() ? '没有搜到相关条目' : '这一卷还是空白的'}</div>
        ) : (
          visibleItems.map(function(item) {
            var kws = Array.isArray(item.keywords) ? item.keywords : [];
            var open = expandedId === item.id;
            return (
              <div key={item.id}>
                <div style={S.row} onClick={function() { openEntry(item); }}>
                  <div style={S.rowTop}>
                    <div
                      style={S.dot(item.is_active)}
                      title={item.is_active ? '点击禁用' : '点击启用'}
                      onClick={function(e) { toggleActive(item, e); }}
                    />
                    <div style={S.rowTitle(item.is_active)}>
                      {item.title}
                      {item.source === 'scene' && <span style={S.sceneBadge}>他写的</span>}
                    </div>
                    <div style={S.rowMeta}>
                      {item.realm === 'aiden' ? '林晏房 · ' : item.realm === 'silas' ? 'Silas房 · ' : ''}{(item.sticky || 0) > 0 ? ('粘' + item.sticky + ' · ') : ''}{activeFolder === '全部' ? item.folder : ''}{(item.hit_count || 0) > 0 ? ((activeFolder === '全部' ? ' · ' : '') + item.hit_count + '次') : ''}
                    </div>
                    <svg style={S.chev(open)} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                  </div>
                  {!open && kws.length > 0 && (
                    <div style={S.kwLine}>
                      {kws.map(function(kw, i) {
                        return <span key={i}><span style={S.kwWord}>{kw}</span>{i < kws.length - 1 ? ' · ' : ''}</span>;
                      })}
                    </div>
                  )}
                </div>
                {open && renderEditor(false)}
              </div>
            );
          })
        )}
      </div>

      {deleteConfirm && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 1001, display: 'flex',
          alignItems: 'center', justifyContent: 'center'
        }} onClick={function() { setDeleteConfirm(null); }}>
          <div style={{
            background: 'var(--card)', borderRadius: 16, padding: '24px 20px',
            width: '80%', maxWidth: 320, textAlign: 'center'
          }} onClick={function(e) { e.stopPropagation(); }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
              确认删除
            </div>
            <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 20, lineHeight: 1.6 }}>
              删除后无法恢复，这份档案会永远消失。
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={function() { setDeleteConfirm(null); }}
                style={{
                  flex: 1, padding: '10px 0', borderRadius: 10, border: '1px solid var(--border)',
                  background: 'var(--bg2)', color: 'var(--text2)', fontSize: 14, cursor: 'pointer',
                  fontFamily: 'var(--font-sans)'
                }}
              >
                取消
              </button>
              <button
                onClick={confirmDelete}
                style={{
                  flex: 1, padding: '10px 0', borderRadius: 10, border: 'none',
                  background: '#e74c3c', color: '#fff', fontSize: 14, cursor: 'pointer',
                  fontFamily: 'var(--font-sans)'
                }}
              >
                删除
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Worldbook;
